'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

// Envelope.tsx renders at a fixed 650×500 (envelope.module.css).
const NATURAL_W = 650;
const NATURAL_H = 500;
// When open, the flap sits at top: -51% of the envelope, so room is reserved above it.
const HEADROOM = 260;
// Breathing room so the stamp at the envelope's edge isn't clipped.
const SIDE_MARGIN = 0.94;

/**
 * Scales the envelope to fit the available width without clipping or scrollbars.
 * A CSS transform alone keeps the 650px layout box, so a narrower parent clips it;
 * here the outer box takes the scaled size and the envelope is scaled from its
 * top-left corner. The box size never depends on the envelope's open state and
 * the wrapper never scrolls, so the width measurement can't feed back into itself
 * (that loop caused the flicker). `zoom` (50–100) shrinks it below the fit.
 */
export default function EnvelopeStage({ zoom = 100, children }: { zoom?: number; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  // Keep the preview and its controls on screen on short laptop displays.
  const [viewportH, setViewportH] = useState(0);

  useEffect(() => {
    const update = () => setViewportH(window.innerHeight);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const next = Math.round(entry.contentRect.width);
      setWidth((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ~300px covers the site header, sticky offset, padding and the zoom/replay bar.
  const heightFit = viewportH ? Math.max(0.35, (viewportH - 300) / (NATURAL_H + HEADROOM)) : 1;
  const fit = width ? Math.min(1, (width * SIDE_MARGIN) / NATURAL_W, heightFit) : 0;
  const scale = fit * (Math.min(zoom, 100) / 100);

  return (
    <div ref={ref} className="flex w-full justify-center overflow-hidden">
      {width > 0 && (
        <div style={{ width: NATURAL_W * scale, height: (NATURAL_H + HEADROOM) * scale }} className="relative shrink-0">
          <div
            style={{ width: NATURAL_W, height: NATURAL_H, top: HEADROOM * scale, transform: `scale(${scale})`, transformOrigin: 'top left' }}
            className="absolute left-0"
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
}
